/**
 * Sección "Sesiones activas" — lista todas las sesiones, marca la
 * actual, permite revocar individual y "cerrar todas las demás".
 */

import { toast } from 'sonner'
import { Monitor, ShieldX } from 'lucide-react'

import {
  ActionButton,
  InlineAlert,
  SettingsCard,
  SettingsSection,
} from '@/components/ui/settings-primitives'
import {
  useRevokeAllOtherSessions,
  useRevokeSession,
  useSessions,
} from '@/hooks/api/use-profile'

export function SessionsSection() {
  const { data, isLoading } = useSessions()
  const revoke = useRevokeSession()
  const revokeAll = useRevokeAllOtherSessions()

  if (isLoading || !data) {
    return (
      <SettingsCard className="p-6">
        <div className="h-32 animate-pulse rounded bg-bg-elevated" />
      </SettingsCard>
    )
  }

  const active = data.sessions.filter((s) => s.is_active)
  const revoked = data.sessions.filter((s) => !s.is_active)
  const others = active.filter((s) => !s.is_current)

  const onRevoke = async (id: string) => {
    try {
      await revoke.mutateAsync(id)
      toast.success('Sesión revocada')
    } catch {
      toast.error('No pudimos revocar la sesión')
    }
  }

  const onRevokeAll = async () => {
    try {
      const r = await revokeAll.mutateAsync()
      toast.success(`${r.revoked} sesiones cerradas`)
    } catch {
      toast.error('No pudimos cerrar las otras sesiones')
    }
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Sesiones activas"
        description="Cada vez que inicias sesión en un dispositivo se crea una sesión. Cierra las que no reconozcas."
        footer={
          others.length > 0 ? (
            <ActionButton variant="danger" onClick={onRevokeAll} loading={revokeAll.isPending}>
              <ShieldX className="h-4 w-4" />
              Cerrar las otras {others.length} {others.length === 1 ? 'sesión' : 'sesiones'}
            </ActionButton>
          ) : null
        }
      >
        {active.length === 0 ? (
          <InlineAlert variant="info">No hay sesiones activas.</InlineAlert>
        ) : (
          <ul className="divide-y divide-border">
            {active.map((s) => (
              <li key={s.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <Monitor className="mt-0.5 h-5 w-5 shrink-0 text-fg-muted" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <p className="text-sm font-medium text-fg-primary">
                      {parseUserAgent(s.user_agent)}
                    </p>
                    {s.is_current && (
                      <span className="rounded-full bg-success-tint px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success-fg">
                        Sesión actual
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    {s.ip_address ?? '—'} · iniciada {fmt(s.created_at)}
                    {s.last_activity_at && ` · última actividad ${fmt(s.last_activity_at)}`}
                  </p>
                </div>
                {!s.is_current && (
                  <ActionButton
                    variant="ghost"
                    onClick={() => onRevoke(s.id)}
                    disabled={revoke.isPending}
                  >
                    Cerrar
                  </ActionButton>
                )}
              </li>
            ))}
          </ul>
        )}
      </SettingsSection>

      {revoked.length > 0 && (
        <SettingsSection
          title="Sesiones cerradas recientemente"
          description="Para auditoría — las últimas 50 sesiones, incluidas las ya cerradas."
        >
          <ul className="divide-y divide-border">
            {revoked.slice(0, 10).map((s) => (
              <li key={s.id} className="py-2.5 text-sm first:pt-0 last:pb-0">
                <p className="text-fg-secondary">{parseUserAgent(s.user_agent)}</p>
                <p className="text-xs text-fg-muted">
                  {s.ip_address ?? '—'} · cerrada {fmt(s.revoked_at)}
                  {s.revoked_reason && ` (${s.revoked_reason})`}
                </p>
              </li>
            ))}
          </ul>
        </SettingsSection>
      )}
    </div>
  )
}

function fmt(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('es-CO', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

function parseUserAgent(ua: string | null): string {
  if (!ua) return 'Navegador desconocido'
  const lc = ua.toLowerCase()
  let browser = 'Navegador'
  if (lc.includes('edg/')) browser = 'Edge'
  else if (lc.includes('chrome/')) browser = 'Chrome'
  else if (lc.includes('firefox/')) browser = 'Firefox'
  else if (lc.includes('safari/')) browser = 'Safari'
  let os = ''
  if (lc.includes('mac os')) os = 'macOS'
  else if (lc.includes('windows')) os = 'Windows'
  else if (lc.includes('linux')) os = 'Linux'
  else if (lc.includes('android')) os = 'Android'
  else if (lc.includes('iphone') || lc.includes('ipad')) os = 'iOS'
  return os ? `${browser} · ${os}` : browser
}
